import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatCurrency, formatDateBR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, FileBarChart2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

const MESES = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

function currentMonthISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function inMonth(dateStr, mes) {
    if (!dateStr || !mes) return false;
    return dateStr.startsWith(mes);
}

// Palavras-chave em modelo que indicam o tipo de veículo
const MODELO_CAMINHONETE = ["hilux", "ranger", "l200", "l 200", "amarok", "s10", "s-10", "frontier", "triton", "trito", "duster", "oroch", "rampage", "strada", "camionete", "caminhonete", "pickup", "pick-up"];
const MODELO_ONIBUS = ["marcopolo", "mpolo", "torino", "paradiso", "busscar", "comil", "neobus", "onibus", "ônibus", "m.benz", "mercedes-benz onibus"];

function inferirTipoVeiculo(modelo) {
    const m = (modelo || "").toLowerCase();
    if (!m) return null;
    for (const kw of MODELO_ONIBUS) if (m.includes(kw)) return "onibus";
    for (const kw of MODELO_CAMINHONETE) if (m.includes(kw)) return "caminhonete";
    return null;
}

const RATE_BY_TIPO = { caminhonete: 12000, onibus: 21000 };

// Parcela mensal de um contrato fixo (R$/mês).
// Detecta o tipo do veículo pelo modelo e aplica o valor-tabela:
//   caminhonete = R$ 12.000  •  ônibus = R$ 21.000
// Se não conseguir inferir, usa valor_diaria do banco (ou valor/dias como fallback).
function parcelaMensalFixo(t) {
    if (!t) return 0;
    if (t.tipo !== "fixo") return t.valor || 0;
    const tipoV = inferirTipoVeiculo(t.veiculo_modelo);
    if (tipoV && RATE_BY_TIPO[tipoV]) return RATE_BY_TIPO[tipoV];
    const dias = Math.max(1, parseInt(t.dias) || 1);
    const vd = Number(t.valor_diaria) || 0;
    if (vd > 0) return vd;
    const total = Number(t.valor) || 0;
    return total / dias;
}

function tripDatesInMonth(t, mes) {
    // Para Contratos de Veículos (fixo) com período definido, aparece em TODOS os meses do contrato
    // (checar primeiro para garantir que contratos que abrangem múltiplos meses,
    // inclusive o mês final como agosto, sejam sempre incluídos no relatório)
    if (t.tipo === "fixo" && t.contrato_inicio && t.contrato_fim) {
        const mesInicio = t.contrato_inicio.slice(0, 7);
        const mesFim = t.contrato_fim.slice(0, 7);
        return mes >= mesInicio && mes <= mesFim;
    }
    if (t.datas && t.datas.length > 0) {
        return t.datas.some((d) => inMonth(d, mes));
    }
    return inMonth(t.data, mes);
}

export default function RelatorioMensal() {
    const { user } = useAuth();
    const [mes, setMes] = useState(currentMonthISO());
    const [loading, setLoading] = useState(true);
    const [trips, setTrips] = useState([]);
    const [requests, setRequests] = useState([]);
    const [freights, setFreights] = useState([]);
    const [fuels, setFuels] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [tRes, rRes, fRes, comRes] = await Promise.all([
                api.get("/trips"),
                api.get("/requests"),
                api.get("/freights"),
                api.get("/fuel-receipts"),
            ]);
            setTrips(tRes.data || []);
            setRequests(rRes.data || []);
            setFreights(fRes.data || []);
            setFuels(comRes.data || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const data = useMemo(() => {
        const tripsMes = trips.filter((t) => tripDatesInMonth(t, mes));
        const fixos = tripsMes.filter((t) => t.tipo === "fixo");
        const diarias = tripsMes.filter((t) => t.tipo === "diaria");
        const reqsMes = requests.filter((r) => inMonth(r.data_viagem, mes));
        const freightsMes = freights.filter((f) => inMonth(f.data, mes));
        const fuelsMes = fuels.filter((f) => inMonth(f.data, mes));

        // Contratos de longo prazo: fixos com contrato_inicio/fim abrangendo >= 2 meses
        const contratosLongos = fixos.filter((t) => {
            if (!t.contrato_inicio || !t.contrato_fim) return false;
            const [yi, mi] = t.contrato_inicio.split("-").map(Number);
            const [yf, mf] = t.contrato_fim.split("-").map(Number);
            const meses = (yf - yi) * 12 + (mf - mi) + 1;
            return meses >= 2;
        }).map((t) => {
            const [yi, mi] = t.contrato_inicio.split("-").map(Number);
            const [yf, mf] = t.contrato_fim.split("-").map(Number);
            const totalMeses = (yf - yi) * 12 + (mf - mi) + 1;
            const [ya, ma] = mes.split("-").map(Number);
            const parcela = (ya - yi) * 12 + (ma - mi) + 1;
            return { ...t, _totalMeses: totalMeses, _parcela: parcela };
        });

        // Para contratos fixos (longo prazo), usamos somente a PARCELA MENSAL no total do mês.
        // Para fixos sem contrato definido, usamos o valor cheio (viagem única).
        const totalFixos = fixos.reduce((a, t) => {
            if (t.contrato_inicio && t.contrato_fim) return a + parcelaMensalFixo(t);
            return a + (t.valor || 0);
        }, 0);
        const totalDiarias = diarias.reduce((a, t) => a + (t.valor || 0), 0);
        const totalFretes = freightsMes.reduce((a, f) => a + (f.valor || 0), 0);
        const totalCombustivel = fuelsMes.reduce((a, f) => a + (f.valor_total || 0), 0);
        const totalGeral = totalFixos + totalDiarias + totalFretes + totalCombustivel;
        const totalLitros = fuelsMes.reduce((a, f) => a + (f.litros || 0), 0);

        const kmRodados = tripsMes.reduce((a, t) => {
            if (t.km_saida != null && t.km_chegada != null && t.km_chegada >= t.km_saida) {
                return a + (t.km_chegada - t.km_saida);
            }
            return a;
        }, 0);

        const reqStatus = { pendente: 0, aprovada: 0, negada: 0, concluida: 0 };
        reqsMes.forEach((r) => { reqStatus[r.status] = (reqStatus[r.status] || 0) + 1; });

        const porVinculo = {};
        reqsMes.forEach((r) => {
            const v = r.vinculo || "outro";
            porVinculo[v] = (porVinculo[v] || 0) + 1;
        });

        const porDestino = {};
        [...tripsMes, ...freightsMes].forEach((t) => {
            const d = t.destino;
            // Para contratos fixos de longo prazo, somamos somente a parcela mensal
            const val = (t.tipo === "fixo" && t.contrato_inicio && t.contrato_fim)
                ? parcelaMensalFixo(t)
                : (t.valor || 0);
            porDestino[d] = (porDestino[d] || 0) + val;
        });
        const destinoTop = Object.entries(porDestino).sort((a, b) => b[1] - a[1]).slice(0, 5);

        // Combustível por empreendimento e por posto
        const fuelPorEmpr = {};
        const fuelPorPosto = {};
        fuelsMes.forEach((f) => {
            fuelPorEmpr[f.empreendimento] = (fuelPorEmpr[f.empreendimento] || 0) + (f.valor_total || 0);
            fuelPorPosto[f.posto] = (fuelPorPosto[f.posto] || 0) + (f.valor_total || 0);
        });

        return {
            tripsMes, fixos, diarias, reqsMes, freightsMes, fuelsMes, contratosLongos,
            totalFixos, totalDiarias, totalFretes, totalCombustivel, totalGeral, totalLitros,
            kmRodados, reqStatus, porVinculo, destinoTop, fuelPorEmpr, fuelPorPosto,
        };
    }, [trips, requests, freights, fuels, mes]);

    const [ano, mesNum] = mes.split("-");
    const mesLabel = mesNum ? `${MESES[parseInt(mesNum) - 1].toUpperCase()} DE ${ano}` : "";
    const emissao = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
    const doPrint = () => window.print();

    const VINCULO_LABELS = {
        cooperado: "Cooperado", nao_cooperado: "Não cooperado",
        setor_social: "Setor Social", entidade: "Entidade externa",
        comunidade: "Comunidade", outro: "Outro",
    };

    return (
        <div data-testid="relatorio-mensal-page">
            {/* Controls - no-print */}
            <div className="no-print space-y-4 mb-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                            <FileBarChart2 className="w-7 h-7 text-primary" /> Relatório Mensal Consolidado
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">Documento oficial para apresentação ao Conselho Fiscal</p>
                    </div>
                    <Button onClick={doPrint} className="gap-2" data-testid="btn-print-relatorio">
                        <Printer className="w-4 h-4" /> Imprimir / Salvar PDF
                    </Button>
                </div>
                <Card>
                    <CardContent className="p-4 flex gap-3 items-end flex-wrap">
                        <div>
                            <label className="text-xs font-medium text-muted-foreground block mb-1">Mês de referência</label>
                            <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-56" data-testid="input-relatorio-month" />
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {loading ? "Carregando dados..." : `${data.tripsMes.length + data.reqsMes.length + data.freightsMes.length} registros no período`}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : (
                <div className="relatorio bg-white shadow-2xl max-w-[210mm] mx-auto" data-testid="relatorio-body">
                    {/* Top accent bar */}
                    <div className="h-2 bg-gradient-to-r from-emerald-700 to-emerald-400" />

                    {/* Header */}
                    <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white">
                        <div className="w-20 h-20 bg-emerald-900 rounded-xl flex items-center justify-center p-1.5 shrink-0 shadow-lg border-2 border-emerald-800">
                            <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1">
                            <div className="font-extrabold text-lg leading-tight text-emerald-900">COOPERATIVA MISTA DA FLONA DO TAPAJÓS</div>
                            <div className="text-sm font-bold text-emerald-700 tracking-wide">COOMFLONA — Setor de Transporte</div>
                            <div className="text-[11px] mt-1 text-gray-600">CNPJ 07.315.165/0001-00 • Insc. Est: 15-249-128-7 • coomflona@hotmail.com</div>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="bg-emerald-800 text-white text-center py-3">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-100">Relatório Mensal Consolidado</div>
                        <div className="font-extrabold text-lg mt-0.5">REFERÊNCIA: {mesLabel}</div>
                        <div className="text-[10px] text-emerald-100 mt-0.5">Para apresentação ao Conselho Fiscal</div>
                    </div>

                    <div className="p-6 space-y-5">
                        {/* Executive Summary */}
                        <Section title="1. RESUMO EXECUTIVO">
                            <p className="text-sm text-gray-700 leading-relaxed">
                                O presente relatório apresenta o consolidado das atividades do Setor de Transporte da COOMFLONA
                                no mês de <strong>{mesLabel}</strong>. Foram registradas <strong>{data.tripsMes.length}</strong> viagem(ns) executada(s),
                                <strong> {data.reqsMes.length}</strong> solicitação(ões) de transporte, <strong> {data.freightsMes.length}</strong> frete(s) e
                                <strong> {data.fuelsMes.length}</strong> nota(s) de combustível,
                                totalizando o valor movimentado de <strong className="text-emerald-700">{formatCurrency(data.totalGeral)}</strong>{data.kmRodados > 0 && <> e uma quilometragem total percorrida de <strong>{data.kmRodados.toLocaleString("pt-BR")} km</strong></>}.
                            </p>
                        </Section>

                        {/* KPI Grid */}
                        <div className="grid grid-cols-5 gap-2">
                            <Kpi label="Total geral" value={formatCurrency(data.totalGeral)} accent primary />
                            <Kpi label="Contratos" value={formatCurrency(data.totalFixos)} sub={`${data.fixos.length} viagem(ns)`} />
                            <Kpi label="Diárias" value={formatCurrency(data.totalDiarias)} sub={`${data.diarias.length} viagem(ns)`} />
                            <Kpi label="Fretes" value={formatCurrency(data.totalFretes)} sub={`${data.freightsMes.length} frete(s)`} />
                            <Kpi label="Combustível" value={formatCurrency(data.totalCombustivel)} sub={`${data.totalLitros.toFixed(0)} L`} />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Kpi label="Solicitações recebidas" value={String(data.reqsMes.length)} sub={`${data.reqStatus.aprovada || 0} aprovadas • ${data.reqStatus.pendente || 0} pendentes`} />
                            <Kpi label="Solicitações concluídas" value={String(data.reqStatus.concluida || 0)} sub={`${data.reqStatus.negada || 0} negada(s)`} />
                            <Kpi label="KM total rodado" value={`${data.kmRodados.toLocaleString("pt-BR")} km`} sub="Somente viagens com KM registrado" />
                        </div>

                        {/* Section: Viagens */}
                        <Section title="2. VIAGENS EXECUTADAS">
                            {data.tripsMes.length === 0 ? (
                                <div className="text-sm text-gray-500 italic py-2">Nenhuma viagem registrada no período.</div>
                            ) : (
                                <table className="w-full text-xs mt-2">
                                    <thead>
                                        <tr className="bg-emerald-50 text-emerald-900 border-b border-emerald-200">
                                            <th className="py-1.5 px-2 text-left font-semibold">Data</th>
                                            <th className="py-1.5 px-2 text-left font-semibold">Tipo</th>
                                            <th className="py-1.5 px-2 text-left font-semibold">Responsável</th>
                                            <th className="py-1.5 px-2 text-left font-semibold">Trajeto</th>
                                            <th className="py-1.5 px-2 text-left font-semibold">Placa</th>
                                            <th className="py-1.5 px-2 text-center font-semibold">Dias</th>
                                            <th className="py-1.5 px-2 text-right font-semibold">Valor (R$)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.tripsMes.map((t) => {
                                            const isContrato = t.tipo === "fixo" && t.contrato_inicio && t.contrato_fim;
                                            const valorExibido = isContrato ? parcelaMensalFixo(t) : (t.valor || 0);
                                            return (
                                                <tr key={t.id} className="border-b border-gray-100">
                                                    <td className="py-1 px-2 font-mono">{formatDateBR(t.data)}</td>
                                                    <td className="py-1 px-2">
                                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${t.tipo === "fixo" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                                                            {t.tipo === "fixo" ? "CONTRATO" : "DIÁRIA"}
                                                        </span>
                                                    </td>
                                                    <td className="py-1 px-2">{t.responsavel_nome}</td>
                                                    <td className="py-1 px-2 text-[11px]">
                                                        {t.local_saida && <span className="text-gray-500">{t.local_saida} → </span>}
                                                        <strong>{t.destino}</strong>
                                                    </td>
                                                    <td className="py-1 px-2 font-mono text-[11px]">{t.veiculo_placa}</td>
                                                    <td className="py-1 px-2 text-center">{t.dias || 1}</td>
                                                    <td className="py-1 px-2 text-right font-bold">
                                                        {formatCurrency(valorExibido)}
                                                        {isContrato && <div className="text-[8px] text-gray-500 font-normal">parcela do mês</div>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="bg-emerald-800 text-white font-bold">
                                            <td colSpan={6} className="py-1.5 px-2 text-right">TOTAL VIAGENS:</td>
                                            <td className="py-1.5 px-2 text-right">{formatCurrency(data.totalFixos + data.totalDiarias)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </Section>

                        {/* Section: Fretes */}
                        <Section title="3. FRETES">
                            {data.freightsMes.length === 0 ? (
                                <div className="text-sm text-gray-500 italic py-2">Nenhum frete registrado no período.</div>
                            ) : (
                                <table className="w-full text-xs mt-2">
                                    <thead>
                                        <tr className="bg-emerald-50 text-emerald-900 border-b border-emerald-200">
                                            <th className="py-1.5 px-2 text-left font-semibold">Data</th>
                                            <th className="py-1.5 px-2 text-left font-semibold">Cliente</th>
                                            <th className="py-1.5 px-2 text-left font-semibold">Trajeto</th>
                                            <th className="py-1.5 px-2 text-right font-semibold">Valor (R$)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.freightsMes.map((f) => (
                                            <tr key={f.id} className="border-b border-gray-100">
                                                <td className="py-1 px-2 font-mono">{formatDateBR(f.data)}</td>
                                                <td className="py-1 px-2">{f.cliente_nome}</td>
                                                <td className="py-1 px-2 text-[11px]">{f.origem} → <strong>{f.destino}</strong></td>
                                                <td className="py-1 px-2 text-right font-bold">{formatCurrency(f.valor)}</td>
                                            </tr>
                                        ))}
                                        <tr className="bg-emerald-800 text-white font-bold">
                                            <td colSpan={3} className="py-1.5 px-2 text-right">TOTAL FRETES:</td>
                                            <td className="py-1.5 px-2 text-right">{formatCurrency(data.totalFretes)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </Section>

                        {/* Section: Contratos de longo prazo */}
                        {data.contratosLongos.length > 0 && (
                            <Section title="3.1. CONTRATOS DE VEÍCULOS — PARCELAS DO MÊS">
                                <div className="bg-blue-50 border-l-4 border-blue-600 rounded p-3 my-2 text-[11px] text-gray-800 leading-relaxed">
                                    <div className="font-bold text-blue-900 mb-1">📋 Como ler esta seção</div>
                                    Contratos com veículos que possuem duração maior ou igual a <strong>2 meses</strong>.
                                    A coluna <strong>&quot;Parcela do mês&quot;</strong> mostra qual mensalidade estamos pagando
                                    (ex: <span className="px-1.5 py-0.5 rounded bg-blue-200 text-blue-900 font-bold text-[10px]">2/3</span> = 2ª parcela de um contrato de 3 meses).
                                    O <strong>valor de cada parcela</strong> corresponde ao lançamento feito no mês vigente.
                                </div>
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-blue-50 text-blue-900 border-b-2 border-blue-300">
                                            <th className="py-1.5 px-2 text-left font-bold">Responsável / Motorista</th>
                                            <th className="py-1.5 px-2 text-left font-bold">Placa</th>
                                            <th className="py-1.5 px-2 text-left font-bold">Período do contrato</th>
                                            <th className="py-1.5 px-2 text-center font-bold">Duração</th>
                                            <th className="py-1.5 px-2 text-center font-bold">Parcela<br/>do mês</th>
                                            <th className="py-1.5 px-2 text-center font-bold">Meses<br/>faltantes</th>
                                            <th className="py-1.5 px-2 text-right font-bold">Valor da<br/>parcela (R$)</th>
                                            <th className="py-1.5 px-2 text-right font-bold">Total do<br/>contrato (est.)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.contratosLongos.map((c) => {
                                            const parcela = parcelaMensalFixo(c);
                                            const totalEstimado = parcela * c._totalMeses;
                                            const mesesRestantes = c._totalMeses - c._parcela;
                                            const parcelaColor = c._parcela === c._totalMeses ? "bg-emerald-200 text-emerald-900" : c._parcela === 1 ? "bg-yellow-200 text-yellow-900" : "bg-blue-200 text-blue-900";
                                            return (
                                                <tr key={c.id} className="border-b border-gray-100">
                                                    <td className="py-1.5 px-2">
                                                        <div className="font-semibold">{c.responsavel_nome}</div>
                                                        {c.veiculo_modelo && <div className="text-[10px] text-gray-600">{c.veiculo_modelo}</div>}
                                                    </td>
                                                    <td className="py-1.5 px-2 font-mono text-[11px]">{c.veiculo_placa}</td>
                                                    <td className="py-1.5 px-2 text-[11px]">
                                                        <div><strong>Início:</strong> {formatDateBR(c.contrato_inicio)}</div>
                                                        <div><strong>Fim:</strong> {formatDateBR(c.contrato_fim)}</div>
                                                    </td>
                                                    <td className="py-1.5 px-2 text-center">
                                                        <span className="text-sm font-bold text-blue-900">{c._totalMeses}</span>
                                                        <div className="text-[9px] text-gray-600">{c._totalMeses === 1 ? "mês" : "meses"}</div>
                                                    </td>
                                                    <td className="py-1.5 px-2 text-center">
                                                        <span className={`inline-block px-2 py-1 rounded ${parcelaColor} text-sm font-extrabold`}>
                                                            {c._parcela}/{c._totalMeses}
                                                        </span>
                                                        {c._parcela === c._totalMeses && <div className="text-[9px] text-emerald-700 mt-0.5 font-bold">ÚLTIMA</div>}
                                                        {c._parcela === 1 && <div className="text-[9px] text-yellow-700 mt-0.5 font-bold">PRIMEIRA</div>}
                                                    </td>
                                                    <td className="py-1.5 px-2 text-center">
                                                        {mesesRestantes > 0 ? (
                                                            <>
                                                                <span className="text-lg font-extrabold text-orange-700">{mesesRestantes}</span>
                                                                <div className="text-[9px] text-orange-800 font-bold">{mesesRestantes === 1 ? "mês faltante" : "meses faltantes"}</div>
                                                            </>
                                                        ) : (
                                                            <span className="inline-block px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-extrabold">FINALIZADO</span>
                                                        )}
                                                    </td>
                                                    <td className="py-1.5 px-2 text-right font-bold text-emerald-800">{formatCurrency(parcela)}</td>
                                                    <td className="py-1.5 px-2 text-right text-gray-700">{formatCurrency(totalEstimado)}</td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="bg-blue-800 text-white font-bold">
                                            <td colSpan={6} className="py-1.5 px-2 text-right">TOTAL PAGO EM PARCELAS DE CONTRATOS NESTE MÊS:</td>
                                            <td colSpan={2} className="py-1.5 px-2 text-right text-base">
                                                {formatCurrency(data.contratosLongos.reduce((a, c) => a + parcelaMensalFixo(c), 0))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Section>
                        )}

                        {/* Section: Combustível */}
                        <Section title="3.2. NOTAS DE COMBUSTÍVEL">
                            {data.fuelsMes.length === 0 ? (
                                <div className="text-sm text-gray-500 italic py-2">Nenhuma nota de combustível no período.</div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-3 gap-2 mb-2">
                                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                                            <div className="text-[9px] uppercase text-emerald-700 font-bold">Total gasto</div>
                                            <div className="text-lg font-extrabold text-emerald-900">{formatCurrency(data.totalCombustivel)}</div>
                                        </div>
                                        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                                            <div className="text-[9px] uppercase text-blue-700 font-bold">Litros</div>
                                            <div className="text-lg font-extrabold text-blue-900">{data.totalLitros.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} L</div>
                                        </div>
                                        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-center">
                                            <div className="text-[9px] uppercase text-gray-700 font-bold">Notas</div>
                                            <div className="text-lg font-extrabold">{data.fuelsMes.length}</div>
                                        </div>
                                    </div>
                                    <div className="mb-2 text-[11px]">
                                        <strong>Por empreendimento:</strong>{" "}
                                        {Object.entries(data.fuelPorEmpr).map(([k, v]) => {
                                            const emprLabels = { km117: "KM 117", rio_cupari: "Rio Cupari", sede: "Sede", setor_social: "Setor Social", nao_madeireiro: "Não Madeireiro", outro: "Outro" };
                                            return (
                                                <span key={k} className="inline-block mx-1">
                                                    {emprLabels[k]}: <strong className="text-emerald-800">{formatCurrency(v)}</strong>
                                                </span>
                                            );
                                        })}
                                    </div>
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-emerald-50 text-emerald-900 border-b border-emerald-200">
                                                <th className="py-1.5 px-2 text-left font-semibold">Data</th>
                                                <th className="py-1.5 px-2 text-left font-semibold">Posto</th>
                                                <th className="py-1.5 px-2 text-left font-semibold">Comb.</th>
                                                <th className="py-1.5 px-2 text-left font-semibold">Placa</th>
                                                <th className="py-1.5 px-2 text-left font-semibold">Empr.</th>
                                                <th className="py-1.5 px-2 text-right font-semibold">Litros</th>
                                                <th className="py-1.5 px-2 text-right font-semibold">Valor (R$)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.fuelsMes.map((f) => {
                                                const comb = { diesel: "Diesel", diesel_s10: "S10", gasolina: "Gasol.", etanol: "Etanol", gnv: "GNV", outro: "—" };
                                                const empr = { km117: "KM 117", rio_cupari: "R.Cupari", sede: "Sede", setor_social: "Social", nao_madeireiro: "Não Mad.", outro: "Outro" };
                                                return (
                                                    <tr key={f.id} className="border-b border-gray-100">
                                                        <td className="py-1 px-2 font-mono">{formatDateBR(f.data)}</td>
                                                        <td className="py-1 px-2">{f.posto}</td>
                                                        <td className="py-1 px-2 text-[11px]">{comb[f.tipo_combustivel]}</td>
                                                        <td className="py-1 px-2 font-mono text-[11px]">{f.veiculo_placa}</td>
                                                        <td className="py-1 px-2 text-[11px]">{empr[f.empreendimento]}</td>
                                                        <td className="py-1 px-2 text-right">{(f.litros || 0).toFixed(2).replace(".", ",")}</td>
                                                        <td className="py-1 px-2 text-right font-bold">{formatCurrency(f.valor_total)}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className="bg-emerald-800 text-white font-bold">
                                                <td colSpan={5} className="py-1.5 px-2 text-right">TOTAL COMBUSTÍVEL:</td>
                                                <td className="py-1 px-2 text-right">{data.totalLitros.toFixed(2).replace(".", ",")} L</td>
                                                <td className="py-1.5 px-2 text-right">{formatCurrency(data.totalCombustivel)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </Section>

                        {/* Section: Solicitações */}
                        <Section title="4. SOLICITAÇÕES DE TRANSPORTE">
                            {data.reqsMes.length === 0 ? (
                                <div className="text-sm text-gray-500 italic py-2">Nenhuma solicitação no período.</div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-4 gap-2 mb-3">
                                        <MiniStat label="Pendentes" value={data.reqStatus.pendente || 0} color="orange" />
                                        <MiniStat label="Aprovadas" value={data.reqStatus.aprovada || 0} color="emerald" />
                                        <MiniStat label="Concluídas" value={data.reqStatus.concluida || 0} color="blue" />
                                        <MiniStat label="Negadas" value={data.reqStatus.negada || 0} color="red" />
                                    </div>
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-emerald-50 text-emerald-900 border-b border-emerald-200">
                                                <th className="py-1.5 px-2 text-left font-semibold">Data</th>
                                                <th className="py-1.5 px-2 text-left font-semibold">Solicitante</th>
                                                <th className="py-1.5 px-2 text-left font-semibold">Vínculo</th>
                                                <th className="py-1.5 px-2 text-left font-semibold">Destino</th>
                                                <th className="py-1.5 px-2 text-center font-semibold">Status</th>
                                                <th className="py-1.5 px-2 text-right font-semibold">Valor Est.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.reqsMes.map((r) => (
                                                <tr key={r.id} className="border-b border-gray-100">
                                                    <td className="py-1 px-2 font-mono">{formatDateBR(r.data_viagem)}</td>
                                                    <td className="py-1 px-2">{r.solicitante_nome}</td>
                                                    <td className="py-1 px-2 text-[11px]">{VINCULO_LABELS[r.vinculo] || "—"}</td>
                                                    <td className="py-1 px-2 text-[11px]">{r.destino}</td>
                                                    <td className="py-1 px-2 text-center">
                                                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-100">
                                                            {r.status}
                                                        </span>
                                                    </td>
                                                    <td className="py-1 px-2 text-right">{r.valor_estimado > 0 ? formatCurrency(r.valor_estimado) : "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </Section>

                        {/* Top destinos */}
                        {data.destinoTop.length > 0 && (
                            <Section title="5. TOP 5 DESTINOS POR VALOR MOVIMENTADO">
                                <div className="space-y-1.5 mt-2">
                                    {data.destinoTop.map(([dest, val], idx) => {
                                        const max = data.destinoTop[0][1];
                                        const pct = max > 0 ? (val / max) * 100 : 0;
                                        return (
                                            <div key={dest} className="text-xs">
                                                <div className="flex justify-between mb-0.5">
                                                    <span><strong>{idx + 1}.</strong> {dest}</span>
                                                    <strong className="text-emerald-700">{formatCurrency(val)}</strong>
                                                </div>
                                                <div className="h-2 bg-emerald-100 rounded overflow-hidden">
                                                    <div className="h-full bg-emerald-600 rounded" style={{ width: `${pct}%` }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Section>
                        )}

                        {/* Fechamento */}
                        <Section title="6. CONSIDERAÇÕES FINAIS">
                            <p className="text-sm text-gray-700 leading-relaxed">
                                Declaramos que todas as informações contidas neste relatório refletem fielmente os registros
                                do Setor de Transporte da COOMFLONA no período de referência. Todos os documentos
                                comprobatórios (fichas de viagem, solicitações e comprovantes) encontram-se arquivados
                                e à disposição para consulta do Conselho Fiscal.
                            </p>
                        </Section>

                        {/* Signatures */}
                        <div className="pt-12 pb-2 grid grid-cols-3 gap-4">
                            <div className="text-center">
                                <div className="border-t-2 border-black pt-1 text-[11px] font-semibold">Setor de Transporte</div>
                                <div className="text-[10px] text-gray-600 mt-0.5">{user?.name || "—"}</div>
                            </div>
                            <div className="text-center">
                                <div className="border-t-2 border-black pt-1 text-[11px] font-semibold">Conselho Fiscal</div>
                                <div className="text-[10px] text-gray-600 mt-0.5">COOMFLONA</div>
                            </div>
                            <div className="text-center">
                                <div className="border-t-2 border-black pt-1 text-[11px] font-semibold">Presidente</div>
                                <div className="text-[10px] text-gray-600 mt-0.5">COOMFLONA</div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-2 border-t border-gray-200 bg-gray-50 flex justify-between items-center text-[9px] text-gray-500">
                        <div>Emitido em: <strong>{emissao}</strong>{user?.name ? ` — por ${user.name}` : ""}</div>
                        <div>Sistema desenvolvido por <strong>Railan Farias</strong> (Cooperado)</div>
                    </div>

                    {/* Bottom accent bar */}
                    <div className="h-1 bg-gradient-to-r from-emerald-400 to-emerald-700" />
                </div>
            )}

            <style>{`
                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    .relatorio { box-shadow: none !important; max-width: 100% !important; }
                    aside, header, nav { display: none !important; }
                    main { padding: 0 !important; }
                    @page { size: A4 portrait; margin: 10mm; }
                }
            `}</style>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="border-l-4 border-emerald-800 pl-2 mb-2">
                <div className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-800">{title}</div>
            </div>
            {children}
        </div>
    );
}

function Kpi({ label, value, sub, accent, primary }) {
    return (
        <div className={`rounded-lg p-3 border ${accent ? "border-emerald-500 shadow-md" : "border-gray-200"} ${primary ? "bg-gradient-to-br from-emerald-600 to-emerald-800 text-white" : "bg-white"}`}>
            <div className={`text-[9px] uppercase tracking-widest font-semibold ${primary ? "text-emerald-100" : "text-gray-500"}`}>{label}</div>
            <div className={`text-lg font-extrabold mt-1 ${primary ? "text-white" : "text-gray-900"}`}>{value}</div>
            {sub && <div className={`text-[9px] mt-0.5 ${primary ? "text-emerald-100" : "text-gray-500"}`}>{sub}</div>}
        </div>
    );
}

function MiniStat({ label, value, color }) {
    const colors = {
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
        orange: "bg-orange-50 text-orange-800 border-orange-200",
        blue: "bg-blue-50 text-blue-800 border-blue-200",
        red: "bg-red-50 text-red-800 border-red-200",
    };
    return (
        <div className={`rounded p-2 border text-center ${colors[color] || colors.emerald}`}>
            <div className="text-lg font-extrabold">{value}</div>
            <div className="text-[9px] uppercase tracking-wide font-semibold">{label}</div>
        </div>
    );
}
